import { MAX_PADDLE_ANGLE, MIN_VY_RATIO } from './constants';
import type { Ball, Rect } from './types';

export interface Hit {
  hit: true;
  nx: number;
  ny: number;
  depth: number;
}

export function circleAabb(
  x: number,
  y: number,
  r: number,
  rect: Rect,
): Hit | { hit: false } {
  const cx = Math.max(rect.x, Math.min(x, rect.x + rect.w));
  const cy = Math.max(rect.y, Math.min(y, rect.y + rect.h));
  const dx = x - cx;
  const dy = y - cy;
  const d2 = dx * dx + dy * dy;
  if (d2 > r * r) return { hit: false };
  const d = Math.sqrt(d2) || 0.0001;
  return { hit: true, nx: dx / d, ny: dy / d, depth: r - d };
}

export function bounceNormal(vx: number, vy: number, nx: number, ny: number): { vx: number; vy: number } {
  const dot = vx * nx + vy * ny;
  return { vx: vx - 2 * dot * nx, vy: vy - 2 * dot * ny };
}

export function paddleBounce(
  ball: Pick<Ball, 'x' | 'vx' | 'vy'>,
  paddle: Rect,
  maxAngle = MAX_PADDLE_ANGLE,
): { vx: number; vy: number } {
  const speed = Math.hypot(ball.vx, ball.vy) || 1;
  const offset = ((ball.x - (paddle.x + paddle.w / 2)) / (paddle.w / 2)) || 0;
  const clamped = Math.max(-1, Math.min(1, offset));
  const angle = -Math.PI / 2 + clamped * maxAngle;
  return { vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed };
}

/** Keep a minimum vertical component so the ball cannot stall horizontally. */
export function nudgeUnstuck(
  vx: number,
  vy: number,
  minRatio = MIN_VY_RATIO,
): { vx: number; vy: number } {
  const speed = Math.hypot(vx, vy);
  if (speed <= 0) return { vx: 0, vy: -1 };
  const absVy = Math.abs(vy);
  if (absVy / speed >= minRatio) return { vx, vy };
  const sign = vy === 0 ? -1 : Math.sign(vy);
  const minVy = speed * minRatio * sign;
  const vxScale = Math.sqrt(Math.max(0, speed * speed - minVy * minVy));
  const vxSign = vx === 0 ? (Math.random() < 0.5 ? -1 : 1) : Math.sign(vx);
  return { vx: vxSign * vxScale, vy: minVy };
}

export function circleObb(
  x: number,
  y: number,
  r: number,
  cx: number,
  cy: number,
  length: number,
  thickness: number,
  angle: number,
): Hit | { hit: false } {
  const cos = Math.cos(-angle);
  const sin = Math.sin(-angle);
  const dx = x - cx;
  const dy = y - cy;
  const lx = dx * cos - dy * sin;
  const ly = dx * sin + dy * cos;
  const local = circleAabb(lx, ly, r, {
    x: -length / 2,
    y: -thickness / 2,
    w: length,
    h: thickness,
  });
  if (!local.hit) return local;
  const nx = local.nx * Math.cos(angle) - local.ny * Math.sin(angle);
  const ny = local.nx * Math.sin(angle) + local.ny * Math.cos(angle);
  return { hit: true, nx, ny, depth: local.depth };
}

export function aabbOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
